import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import api from "../../const/axios";
import { signedIn } from "../../redux/actions";
import GoogleAuthButton from "@/components/Elements/GoogleAuthButton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ErrorBanner, OrDivider, PasswordInput, linkCls } from "./shared";

interface Props {
  onSignedIn: () => void;
  onRegister: () => void;
  onForgot: () => void;
}

const LoginForm: React.FC<Props> = ({ onSignedIn, onRegister, onForgot }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const finish = (data: any) => {
    if (data?.success && data.token) {
      dispatch(signedIn(data));
      onSignedIn();
    } else {
      setError(data?.message || t("auth.operationError"));
      setLoading(false);
    }
  };

  const fail = (err: any, fallback: string) => {
    setError(err?.response?.status === 429 ? t("auth.tooManyAttempts") : fallback);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email.trim()) || !password) {
      setError(t("auth.emailInvalid"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      finish((await api.post("auth/login", { email: email.trim(), password })).data);
    } catch (err) {
      fail(err, t("auth.operationError"));
    }
  };

  const handleGoogleSuccess = async (authCode: string) => {
    setLoading(true);
    setError("");
    try {
      finish((await api.post("auth/google", { authCode })).data);
    } catch (err) {
      fail(err, t("auth.googleAuthFailed"));
    }
  };

  return (
    <div className="space-y-4">
      <ErrorBanner message={error} />

      <GoogleAuthButton
        mode="signin"
        onSuccess={handleGoogleSuccess}
        onError={() => setError(t("auth.googleAuthFailed"))}
        loading={loading}
      />

      <OrDivider label={t("auth.orRegisterWith")} />

      <form onSubmit={handleSubmit} className="space-y-3" noValidate>
        <Input
          type="email"
          aria-label={t("auth.email")}
          placeholder={t("auth.email")}
          value={email}
          onChange={e => { setEmail(e.target.value); setError(""); }}
          autoComplete="email"
          autoFocus
        />
        <PasswordInput
          aria-label={t("auth.password")}
          placeholder={t("auth.password")}
          value={password}
          onChange={e => { setPassword((e.target as HTMLInputElement).value); setError(""); }}
          autoComplete="current-password"
        />
        <Button type="submit" disabled={loading} className="w-full">
          {t("auth.login")}
        </Button>
      </form>

      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onRegister} className={linkCls}>
          {t("auth.register")}
        </button>
        <button type="button" onClick={onForgot} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
          {t("auth.forgotPassword")}
        </button>
      </div>
    </div>
  );
};

export default LoginForm;
